import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Sun, Cloud, CloudSun, CloudRain, Wind, Waves, Moon, Sunrise, Sunset,
  MapPin, RefreshCw, Droplets, ArrowUp, ArrowDown, Loader2, Anchor,
} from 'lucide-react';
import apiV2 from '../utils/apiV2';

const DIRS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
const knots = (ms) => Math.round((ms || 0) * 1.94384);
const windDir = (deg) => (deg == null ? '' : DIRS[Math.round(deg / 22.5) % 16]);
const fmtTemp = (c, unit) => (c == null ? '—' : unit === 'F' ? `${Math.round(c * 9 / 5 + 32)}°` : `${Math.round(c)}°`);
// Payload times are ORG-LOCAL ISO strings — read the wall clock straight off the
// string so a browser in another timezone doesn't shift them.
const fmtClock = (iso) => {
  if (!iso) return '—';
  const h = Number(iso.slice(11, 13)), m = iso.slice(14, 16);
  return `${h % 12 || 12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtHour = (iso) => {
  const h = Number(iso.slice(11, 13));
  return `${h % 12 || 12} ${h >= 12 ? 'PM' : 'AM'}`;
};

const condOf = (h) => {
  if (!h) return { label: 'Clear', Icon: Sun };
  if ((h.precip || 0) >= 0.4) return { label: 'Rain', Icon: CloudRain };
  if ((h.cloud || 0) > 70) return { label: 'Cloudy', Icon: Cloud };
  if ((h.cloud || 0) > 30) return { label: 'Partly cloudy', Icon: CloudSun };
  return { label: 'Sunny', Icon: Sun };
};

const chartUrl = (loc) =>
  `https://fishing-app.gpsnauticalcharts.com/i-boating-fishing-web-app/fishing-marine-charts-navigation.html#13/${loc.lat}/${loc.lng}`;

export const WeatherChip = ({ isAdmin, onOpenSettings }) => {
  const orgId = localStorage.getItem('organizationId');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!orgId) return;
    let dead = false;
    apiV2.get(`/organizations/${orgId}/weather`)
      .then((r) => { if (!dead) setData(r.data); })
      .catch(() => { if (!dead) setData({ error: true }); });
    return () => { dead = true; };
  }, [orgId]);

  const toggleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      if (window.innerWidth < 640) {
        setPos({ top: r.bottom + 12, center: true });
      } else {
        setPos({ top: r.bottom + 12, right: Math.max(12, window.innerWidth - r.right - 8) });
      }
    }
    setOpen((o) => !o);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await apiV2.post(`/organizations/${orgId}/weather/refresh`);
      setData(r.data);
    } catch { /* keep old data */ }
    setRefreshing(false);
  };

  const wx = data?.weather;
  const unit = wx?.units?.temp || 'F';

  const now = useMemo(() => {
    const hours = wx?.hours || [];
    if (!hours.length) return null;
    const nowMs = Date.now();
    let best = hours[0];
    for (const h of hours) {
      if (new Date(h.t).getTime() <= nowMs) best = h; else break;
    }
    return best;
  }, [wx]);

  const nextHours = useMemo(() => {
    const hours = wx?.hours || [];
    const nowMs = Date.now() - 3600e3;
    return hours.filter((h) => new Date(h.t).getTime() >= nowMs).slice(0, 12);
  }, [wx]);

  const nextTides = useMemo(() => {
    const ex = wx?.tide?.extremes || [];
    const nowMs = Date.now();
    return ex.filter((x) => new Date(x.time).getTime() >= nowMs).slice(0, 2);
  }, [wx]);

  if (!data) {
    return (
      <span data-testid="weather-chip-skeleton" className="flex items-center gap-1.5 animate-pulse" aria-hidden="true">
        <span className="w-[18px] h-[18px] rounded-full bg-gray-200" />
        <span className="hidden sm:block h-3 w-14 rounded bg-gray-200" />
      </span>
    );
  }
  if (data.error) return null;

  if (!data.configured) {
    if (!isAdmin) return null;
    return (
      <button
        onClick={onOpenSettings}
        data-testid="weather-setup-chip"
        className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-500 hover:text-sky-600 transition-colors whitespace-nowrap"
      >
        <CloudSun className="w-[18px] h-[18px]" strokeWidth={1.75} />
        <span className="hidden sm:inline">Set up weather</span>
      </button>
    );
  }

  if (!wx || !now) return null;
  const { label: condLabel, Icon: CondIcon } = condOf(now);

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggleOpen}
        data-testid="weather-chip"
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-sky-700 transition-colors whitespace-nowrap"
        title={data.location?.name}
      >
        <CondIcon className="w-[18px] h-[18px] text-sky-500" strokeWidth={1.75} />
        <span>{fmtTemp(now.temp, unit)} · {knots(now.wind)} kn</span>
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[110]" onClick={() => setOpen(false)} />
          <div
            data-testid="weather-popover"
            style={pos?.center
              ? { position: 'fixed', top: pos.top, left: '50%', transform: 'translateX(-50%)' }
              : { position: 'fixed', top: pos?.top ?? 64, right: pos?.right ?? 16 }}
            className="w-[19rem] max-w-[calc(100vw-1.5rem)] z-[120] rounded-2xl bg-[#0f172a] ring-1 ring-white/10 shadow-2xl shadow-black/50 p-4 text-slate-200"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 min-w-0">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate" data-testid="weather-location-name">{data.location?.name}</span>
              </div>
              {isAdmin && (
                <button
                  onClick={refresh}
                  data-testid="weather-refresh-btn"
                  className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors"
                  title="Refresh forecast"
                >
                  {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>

            {/* Now */}
            <div className="flex items-center gap-3 mb-3">
              <CondIcon className="w-9 h-9 text-sky-300" strokeWidth={1.5} />
              <div>
                <div className="text-2xl font-bold text-white leading-none" data-testid="weather-now-temp">
                  {fmtTemp(now.temp, unit)}<span className="text-sm font-semibold text-slate-400 ml-0.5">{unit}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1">{condLabel}{wx.stale ? ' · cached' : ''}</div>
              </div>
              <div className="ml-auto text-right text-xs space-y-1">
                <div className="flex items-center justify-end gap-1.5 text-slate-300">
                  <Wind className="w-3.5 h-3.5 text-slate-500" />
                  {knots(now.wind)} kn {windDir(now.windDir)}
                </div>
                {now.gust != null && (
                  <div className="text-slate-500">gusts {knots(now.gust)} kn</div>
                )}
                {now.humidity != null && (
                  <div className="flex items-center justify-end gap-1.5 text-slate-500">
                    <Droplets className="w-3 h-3" />{Math.round(now.humidity)}%
                  </div>
                )}
              </div>
            </div>

            {/* Hourly strip */}
            <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1 mb-3" data-testid="weather-hourly-strip">
              {nextHours.map((h) => {
                const { Icon } = condOf(h);
                return (
                  <div key={h.t} className="flex flex-col items-center gap-1 min-w-[2.6rem] rounded-lg bg-white/[0.04] py-1.5">
                    <span className="text-[10px] text-slate-500">{fmtHour(h.t)}</span>
                    <Icon className="w-3.5 h-3.5 text-sky-300" strokeWidth={1.75} />
                    <span className="text-[11px] font-semibold text-slate-200">{fmtTemp(h.temp, unit)}</span>
                  </div>
                );
              })}
            </div>

            {/* Water / waves */}
            {(now.waterTemp != null || now.wave != null) && (
              <div className="flex items-center gap-2 text-xs text-slate-300 mb-2">
                <Waves className="w-3.5 h-3.5 text-slate-500" />
                {now.waterTemp != null && <span>Water {fmtTemp(now.waterTemp, unit)}{unit}</span>}
                {now.wave != null && <span className="text-slate-500">· waves {now.wave.toFixed(1)} m</span>}
              </div>
            )}

            {/* Tide */}
            {wx.tide?.available && nextTides.length > 0 && (
              <div className="flex items-center gap-3 text-xs text-slate-300 mb-2" data-testid="weather-tide-row">
                {nextTides.map((x) => (
                  <span key={x.time} className="flex items-center gap-1">
                    {x.type === 'high'
                      ? <ArrowUp className="w-3 h-3 text-emerald-400" />
                      : <ArrowDown className="w-3 h-3 text-amber-400" />}
                    {x.type === 'high' ? 'High' : 'Low'} {fmtClock(x.time)}
                  </span>
                ))}
              </div>
            )}

            {/* Sun & moon */}
            {wx.astro && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400 border-t border-white/[0.06] pt-2">
                {wx.astro.sunrise && (
                  <span className="flex items-center gap-1"><Sunrise className="w-3 h-3 text-amber-300" />{fmtClock(wx.astro.sunrise)}</span>
                )}
                {wx.astro.sunset && (
                  <span className="flex items-center gap-1"><Sunset className="w-3 h-3 text-orange-400" />{fmtClock(wx.astro.sunset)}</span>
                )}
                {wx.astro.moonPhase && (
                  <span className="flex items-center gap-1" data-testid="weather-moon-phase">
                    <Moon className="w-3 h-3 text-slate-300" />
                    {wx.astro.moonPhase}
                    {wx.astro.moonFraction != null && ` · ${Math.round(wx.astro.moonFraction * 100)}%`}
                  </span>
                )}
              </div>
            )}

            {/* Nautical chart — it's a chart, not a map. */}
            {data.location?.lat != null && (
              <a
                href={chartUrl(data.location)}
                target="_blank"
                rel="noreferrer"
                data-testid="weather-chart-link"
                className="mt-3 flex items-center justify-center gap-1.5 w-full rounded-lg bg-white/[0.06] hover:bg-white/[0.12] text-xs font-semibold text-sky-300 py-2 transition-colors"
              >
                <Anchor className="w-3.5 h-3.5" />
                View nautical chart →
              </a>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default WeatherChip;
